<script lang="ts" setup>
import { useQuery } from '@vue/apollo-composable'
import { computed, ref, watch } from 'vue'
import {
  WdsButtonIcon,
  WdsIcon,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import HireComplianceEndedStatus from './HireComplianceEndedStatus.vue'
import HireComplianceCancelledStatus from './HireComplianceCancelledStatus.vue'
import HireComplianceTerminatedStatus from './HireComplianceTerminatedStatus.vue'
import HireComplianceStatusCompanyModal from './HireComplianceStatusCompanyModal.vue'
import HireComplianceStatusWorkerModal from './HireComplianceStatusWorkerModal.vue'
import HireStatusBox from '@/components/Hire/HirePageParts/HireStatusBox.vue'
import { graphql } from '@/gql'
import { HireActiveStatus } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import HireComplianceDraftStatus from '@/components/Hire/HirePageCompliance/HireComplianceDraftStatus.vue'
import HireComplianceOfferedStatus from '@/components/Hire/HirePageCompliance/HireComplianceOfferedStatus.vue'
import HireComplianceReadyStatus from '@/components/Hire/HirePageCompliance/HireComplianceReadyStatus.vue'
import HireComplianceActiveStatus from '@/components/Hire/HirePageCompliance/HireComplianceActiveStatus.vue'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import { useHireApprovalStatuses } from '@/components/Hire/HirePageCompliance/useHireApprovalStatuses'
import HireComplianceApprovalAlert from '@/components/Hire/HirePageCompliance/HireComplianceApprovalAlert.vue'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'

const props = defineProps<{
  hireId: string
}>()

const { isWorker, isCompany, hasWorkerCapabilities, hasCompanyCapabilities } = useAccountCapabilities()
const { viewerIsSupplier } = useHireRecruiter(props.hireId)
const viewingAsClient = computed(() => isCompany.value && !viewerIsSupplier.value)

const allStatusViews = computed(() =>
  [
    {
      optionKey: HireActiveStatus.Draft,
      component: HireComplianceDraftStatus,
      title: $t('Draft'),
      allowedToShow: viewingAsClient.value,
    },
    {
      optionKey: HireActiveStatus.Offered,
      component: HireComplianceOfferedStatus,
      title: $t('Offered'),
      allowedToShow: hasCompanyCapabilities.value || hasWorkerCapabilities.value,
    },
    {
      optionKey: HireActiveStatus.Ready,
      component: HireComplianceReadyStatus,
      title: $t('Ready'),
      allowedToShow: hasCompanyCapabilities.value || hasWorkerCapabilities.value,
    },
    {
      optionKey: HireActiveStatus.Active,
      component: HireComplianceActiveStatus,
      title: $t('Active'),
      allowedToShow: hasCompanyCapabilities.value || hasWorkerCapabilities.value,
    },
    {
      optionKey: HireActiveStatus.Ended,
      component: HireComplianceEndedStatus,
      title: $t('Ended'),
      allowedToShow: hasCompanyCapabilities.value || hasWorkerCapabilities.value,
    },
    {
      optionKey: HireActiveStatus.Cancelled,
      component: HireComplianceCancelledStatus,
      title: $t('Cancelled'),
      allowedToShow: hasCompanyCapabilities.value || hasWorkerCapabilities.value,
    },
    {
      optionKey: HireActiveStatus.Terminated,
      component: HireComplianceTerminatedStatus,
      title: $t('Terminated'),
      allowedToShow: hasCompanyCapabilities.value || hasWorkerCapabilities.value,
    },
  ].filter(({ allowedToShow }) => allowedToShow),
)

// Single status states that should be shown alone (excluding Active - handled separately)
const singleStatusStates = [
  HireActiveStatus.Ended,
  HireActiveStatus.Cancelled,
  HireActiveStatus.Terminated,
]

const { hasApprovals, loading: approvalInfoLoading } = useHireApprovalStatuses(props.hireId)

const { result: hireResult, loading: hireLoading } = useQuery(
  graphql(`
    query HireStatusWorkflow($id: ID!) {
      hire(id: $id) {
        id
        activeStatus
        activeContract {
          id
          startDate
        }
      }
    }
  `),
  {
    id: props.hireId,
  },
  {
    clientId: 'publicApi',
  },
)

const hire = computed(() => hireResult.value?.hire)
const loading = computed(() => hireLoading.value || approvalInfoLoading.value)

const selectedStatus = ref<HireActiveStatus>(HireActiveStatus.Draft)

const statusComponent = computed(
  () =>
    allStatusViews.value.find((status) => status.optionKey === selectedStatus.value)?.component ??
    null,
)

const statusOrder = [
  HireActiveStatus.Draft,
  HireActiveStatus.Offered,
  HireActiveStatus.Ready,
  HireActiveStatus.Active,
]

watch(
  hire,
  (newHire) => {
    if (newHire?.activeStatus) {
      selectedStatus.value = newHire.activeStatus
    }
  },
  { immediate: true },
)

const isSingleStatus = computed(
  () =>
    singleStatusStates.includes(selectedStatus.value) ||
    selectedStatus.value === HireActiveStatus.Active,
)

const displayedStatuses = computed(() =>
  isSingleStatus.value
    ? allStatusViews.value.filter((status) => status.optionKey === selectedStatus.value)
    : allStatusViews.value.filter((status) => statusOrder.includes(status.optionKey)),
)

const gridClass = computed(() => (isSingleStatus.value ? 'tw-grid-cols-1' : 'tw-grid-cols-4'))

// This will determine if a status is the next possible status
const isNextStatus = (statusKey: HireActiveStatus) => {
  const currentStatusIndex = statusOrder.indexOf(selectedStatus.value)
  const targetStatusIndex = statusOrder.indexOf(statusKey)

  // Returns "true" if the target status is exactly one step after the current status
  return (
    currentStatusIndex !== -1 &&
    targetStatusIndex !== -1 &&
    targetStatusIndex === currentStatusIndex + 1
  )
}

// This will determine if a status is in the future (meaning, is after another step)
const isStatusInFuture = (statusKey: HireActiveStatus) => {
  const currentStatusIndex = statusOrder.indexOf(selectedStatus.value)
  const targetStatusIndex = statusOrder.indexOf(statusKey)

  // Returns "true" if the target status comes after the current status in the sequence
  return (
    currentStatusIndex !== -1 && targetStatusIndex !== -1 && targetStatusIndex > currentStatusIndex
  )
}

const getStatusStyling = (statusKey: HireActiveStatus) => {
  if (statusKey === selectedStatus.value) {
    switch (statusKey) {
      case HireActiveStatus.Draft:
        return { 'tw-bg-gray-600': true }
      case HireActiveStatus.Offered:
        return { 'tw-bg-indigo-50': true }
      case HireActiveStatus.Ready:
      case HireActiveStatus.Active:
        return { 'tw-bg-green-100': true }
      case HireActiveStatus.Ended:
      case HireActiveStatus.Terminated:
        return { 'tw-bg-red-100': true }
      case HireActiveStatus.Cancelled:
        return { 'tw-bg-gray-100': true }
      default:
        return {}
    }
  }

  return {}
}

const getTextStyling = (statusKey: HireActiveStatus) => {
  if (statusKey === selectedStatus.value) {
    switch (statusKey) {
      case HireActiveStatus.Draft:
        return { 'tw-text-white': true }
      case HireActiveStatus.Offered:
        return { 'tw-text-indigo-500': true }
      case HireActiveStatus.Ready:
      case HireActiveStatus.Active:
        return { 'tw-text-green-600': true }
      case HireActiveStatus.Ended:
      case HireActiveStatus.Terminated:
        return { 'tw-text-red-600': true }
      case HireActiveStatus.Cancelled:
        return { 'tw-text-gray-800': true }
      default:
        return {}
    }
  } else if (isNextStatus(statusKey)) {
    return { 'tw-text-gray-600': true }
  } else if (isStatusInFuture(statusKey)) {
    return { 'tw-text-gray-600': true }
  }

  return {}
}

const showLearnMoreModal = ref(false)
</script>

<template>
  <HireStatusBox
    :title="isWorker ? $t('Overview') : $t('Hire status')"
    data-test="compliance-status-widget"
  >
    <template #title-actions>
      <WdsButtonIcon
        transparent-background
        sm
        :title="$t('Learn more')"
        :aria-label="$t('Learn more')"
        @click="showLearnMoreModal = true"
      >
        <WdsIcon name="info" :size="16" class="tw-text-gray-500" />
      </WdsButtonIcon>
    </template>

    <WdsStack>
      <WdsStack v-if="loading" direction-column gap-sm class="tw-w-full">
        <LoadingList :items="3" no-bg />
      </WdsStack>
      <WdsStack v-else-if="hire" direction-column class="tw-w-full">
        <WdsStack class="tw-w-full tw-grid tw-gap-2 tw-mb-4 tw-px-2" :class="gridClass">
          <div
            v-for="(status, index) in displayedStatuses"
            :key="status.optionKey"
            class="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-w-full"
          >
            <WdsStack
              align-center
              justify-center
              class="tw-h-7 tw-rounded-md tw-w-full"
              :class="getStatusStyling(status.optionKey)"
            >
              <WdsText>
                <div :class="getTextStyling(status.optionKey)">
                  {{ status.title }}
                </div>
              </WdsText>
            </WdsStack>

            <WdsIcon
              v-if="index < displayedStatuses.length - 1 && !isSingleStatus"
              name="chevron-right"
              :size="16"
              class="tw-text-gray-600 tw-ml-2"
            />
          </div>
        </WdsStack>

        <WdsStack v-if="statusComponent !== null" direction-column>
          <component :is="statusComponent" :hire-id="hireId">
            <template v-if="hasApprovals && hasCompanyCapabilities" #action>
              <HireComplianceApprovalAlert :hire-id="hireId" />
            </template>
          </component>
        </WdsStack>
      </WdsStack>
    </WdsStack>
  </HireStatusBox>

  <HireComplianceStatusWorkerModal
    v-if="isWorker"
    :open="showLearnMoreModal"
    @close="showLearnMoreModal = false"
  />
  <HireComplianceStatusCompanyModal
    v-else
    :open="showLearnMoreModal"
    @close="showLearnMoreModal = false"
  />
</template>
